import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon, StatusBadge, formatMoney } from '../regureStyles';

const MyCommissionsScreen = () => {
  const { t } = useTranslation();
  const userLogin = useSelector(state => state.userLogin);
  const [commissions, setCommissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;

    const fetchCommissions = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-commissions`, { withCredentials: true });
        setCommissions(response.data?.data || {});
      } catch (err) {
        setError(err.response?.data?.message || t('screens.portal.failedLoadCommissions'));
      } finally {
        setLoading(false);
      }
    };

    fetchCommissions();
  }, [userLogin, apiUrl]);

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  const summary = commissions?.summary || {};
  const history = commissions?.history || [];

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ color: S.burgundy, fontWeight: 700 }}>{t('screens.portal.breadcrumbPortal')}</span> / {t('screens.portal.breadcrumbMyCommissions')}
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
              {t('screens.portal.yourCommissions')}
            </div>
            <div style={{ fontSize: 14, color: S.textMuted, marginTop: 6 }}>
              {t('screens.portal.yourCommissionsSubtitle')}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.totalEarnedYtd')}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary }}>
              {formatMoney(summary.totalEarned, summary.currency)}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.totalEarnedYtdSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.totalPaid')}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>
              {formatMoney(summary.totalPaid, summary.currency)}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.totalPaidSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.outstanding')}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.burgundy }}>
              {formatMoney(summary.totalOutstanding, summary.currency)}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.outstandingSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.commissionRate')}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: S.textPrimary }}>
              {summary.acquisitionRate ? `${(summary.acquisitionRate * 100).toFixed(0)}% / ${(summary.managementRate * 100).toFixed(0)}%` : '—'}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.commissionRateSub')}
            </div>
          </div>
        </div>

        {/* Table Card */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)'
          }}>
            {error}
          </div>
        )}

        <div style={{
          background: '#fff',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 24px',
            borderBottom: `1px solid ${S.borderLight}`,
            background: S.bg
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>
              {t('screens.portal.commissionHistory')}
            </div>
            <button
              style={{
                background: '#fff',
                color: S.textSecondary,
                border: `1px solid ${S.borderLight}`,
                padding: '8px 16px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = S.bg;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#fff';
              }}
            >
              {t('screens.portal.downloadStatement')}
            </button>
          </div>

          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: S.textMuted }}>
              <Icon name="file-text" color={S.borderLight} size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <div style={{ fontSize: 16, fontWeight: 600 }}>{t('screens.portal.noCommissionHistory')}</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    t('screens.portal.commColPeriod'),
                    t('screens.portal.commColGrossPremium'),
                    // Acquisition and Management were separate columns, each
                    // computed in JSX as a fixed percentage of the premium.
                    // The record carries its own type and rate; splitting one
                    // amount into two invented halves is not a breakdown.
                    t('screens.portal.commColType'),
                    t('screens.portal.commColRate'),
                    t('screens.portal.commColTotal'),
                    t('screens.portal.commColStatus'),
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '14px 24px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: S.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        borderBottom: `1px solid ${S.borderLight}`,
                        background: S.bg,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((record, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: `1px solid ${S.borderLight}`,
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = S.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.burgundy, fontWeight: 700 }}>
                      {record.period}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {record.premiumBasis != null ? formatMoney(record.premiumBasis, record.currency) : '—'}
                    </td>
                    {/* The record's own type and applied rate, straight from
                        CommissionRecord. Nothing is derived here. */}
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {record.type ? t(`screens.portal.commType_${record.type}`, { defaultValue: record.type }) : '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {typeof record.commissionRate === 'number' && record.commissionRate > 0
                        ? `${(record.commissionRate * 100).toFixed(2).replace(/\.00$/, '')}%`
                        : '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                      {record.commission != null ? formatMoney(record.commission, record.currency) : '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13 }}>
                      <StatusBadge status={record.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default MyCommissionsScreen;
